import React, { useEffect, useState, useCallback } from "react";
import { Table, Dropdown, Modal, Row, Col, Card, Tooltip, Button } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { doDelete, doPatch } from "../../../../API/apis";
import { useParams } from "react-router-dom";
import PrescriptionItem from "./PrescriptionItem";
import HistoricalMedication from "./HistoricalMedication";
import { useStateGlobalContext } from "../../../../contexts/GlobalContext";
import ViewPrescription from "./ViewPrescription";
import CurrentMedications from "./CurrentMedications";
import Loader from "../../../../components/Loader/Loader";
import PrescriptionPad from "./component/PrescriptionPad";
import showConfirmModal from "../../../../components/ModalConfirm/ModalConfirm";
import Notification from "../../../../components/Notification/Notification";
import FormatDateTime from "../../../../components/FormatDate/FormatDateTime";
import { InvoiceStatus } from "../../../../components/GlobalComponents/StatusLabels";

const items = [
  { key: 1, label: "View" },
  { key: 2, label: "Dispense" },
  { key: 3, label: "Print" },
  { key: 5, label: "Delete" },
];

const AllMedications = () => {
  const { id } = useParams();
  const [view, setView] = useState(false);
  const [viewData, setViewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prescriptionData, setPrescriptionData] = useState(null);
  const [prescriptionDrawer, setPrescriptionDrawer] = useState(false);
  const { getPrescriptions, prescriptions, patientBasicInfo, officeSetting, profile } = useStateGlobalContext();

  const role = profile && profile?.user_info[0]?.role;

  const columns = [
    // {
    //   title: "Rx ID",
    //   dataIndex: "prescription_code",
    //   key: "prescription_code",
    //   render: (text) => (
    //     <span title={text}>
    //       {text.length > 10 ? text.slice(0, 10) + "..." : text}
    //     </span>
    //   ),
    // },
    {
      title: "Medication",
      dataIndex: "prescriptions",
      key: "prescriptions",
      render: (prescriptions, record) => (
        <Button type="link" onClick={() => handleView(record)}>
          {prescriptions.length <= 1 ? (
            prescriptions.map((prescription, index) => (
              <span key={prescription.key} title={prescription?.searched_medicine}>
                {index > 0 ? ", " : ""}
                {prescription?.searched_medicine?.length > 10
                  ? prescription?.searched_medicine?.slice(0, 10) + "..."
                  : prescription?.searched_medicine}
              </span>
            ))
          ) : (
            <>
              <span>
                {prescriptions[0]?.searched_medicine?.length > 10
                  ? prescriptions[0]?.searched_medicine?.slice(0, 10) + "..."
                  : prescriptions[0]?.searched_medicine}
                ,{" "}
              </span>
            </>
          )}
        </Button>
      ),
    },
    {
      title: "Added By",
      dataIndex: "added_by",
      key: "added_by",
      align: "center",
      render: (text) => (
        <span title={text}>
          {text.length > 10 ? text.slice(0, 10) + "..." : text}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date_time",
      key: "date_time",
      align: "center",
      render: (text) => (
        <Tooltip title={<FormatDateTime date={text} type="time" />}>
          <span>{text ? <FormatDateTime date={text} type="date" /> : ""}</span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      // width: "15%",
      align: "center",
      render: (status) =>
        <InvoiceStatus status={status} />
    },

  ];

  if (role !== "biller") {
    columns.push({
      title: "Action",
      key: "action",
      fixed: "right",
      align: "center",
      width: "10%",
      render: (text, record) => {
        const filteredItems = items.filter((item) => {
          // Remove "Dispense" option if the current status is already "Dispensed"
          if (item.key === 2 && record.status === "Dispensed") return false;
          // Remove "Delete" option if the current status is "Dispensed"
          if (item.key === 5 && record.status === "Dispensed") return false;
          return true;
        });

        return (
          <Dropdown
            menu={{
              items: filteredItems.map((item) => ({
                ...item,
                onClick: () => {
                  switch (item.key) {
                    case 1:
                      handleView(record);
                      break;
                    case 2:
                      handleStatus(record, "Dispensed");
                      break;
                    case 3:
                      handlePrint(record);
                      break;
                    case 5:
                      handleDelete(record);
                      break;
                    default:
                      break;
                  }
                },
              })),
            }}
            trigger={["click"]}
          >
            <EllipsisOutlined className="cursor-pointer" />
          </Dropdown>
        );
      },
    });
  }

  const handleView = useCallback((record) => {
    setViewData(record);
    setView(true);
  }, []);

  const handleStatus = async (record, status) => {
    const response = await doPatch(`/prescriptions/update/${id}/${record.id}/`, { status });
    if (response.status === 200) {
      Notification.success("Status updated successfully");
      getPrescriptions(id);
    }
  };

  const handleDelete = (record) => {
    showConfirmModal({
      title: "Confirm Delete?",
      icon: null,
      content: "Are you sure you want to delete this?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      className: "custom-modal",
      onOk: () => deletePrescription(record.id),
    });
  };

  const deletePrescription = async (recordId) => {
    try {
      const response = await doDelete(`/prescriptions/delete/${id}/${recordId}/`);
      if (response.status === 200) {
        Notification.success("Prescription deleted successfully");
        getPrescriptions(id);
      }
    } catch (error) {
      Notification.error("Failed to delete prescription");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await getPrescriptions(id);
        // await getOfficeSettings();
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handlePrint = (record) => {
    setPrescriptionDrawer(true);
    setPrescriptionData(record);
  };

  const onClose = () => {
    setPrescriptionDrawer(false);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xl={12} lg={24} md={24} sm={24}>
          <Card className="shadow-class" title="Prescribed Medications">
            <div className="min-h-[150px] max-h-[250px] overflow-auto">
              {prescriptions?.length === 0 ? (
                <p>No medication added.</p>
              ) : (
                prescriptions?.map((item) => (
                  <PrescriptionItem
                    key={item.id}
                    prescriptions={item?.prescriptions}
                    showButton={true}
                  />
                ))
              )}
            </div>
          </Card>
          <CurrentMedications />
        </Col>
        <Col xl={12} lg={24} md={24} sm={24}>
          <Card className="shadow-class h-[550px]" title="Digital Prescriptions">
            <Table
              columns={columns}
              dataSource={prescriptions}
              pagination={{
                pageSize: 7,
                showTotal: (total) => `Total ${total} items`,
              }}
              loading={loading}
              size="small"
              scroll={{ y: 370 }}
            />
          </Card>

          <Modal
            closeIcon={<Button size="small" className="app-close-icon" shape="round">Close</Button>}
            title={`Rx ID: ${viewData?.prescription_code}`}
            open={view}
            footer={null}
            bodyStyle={{ maxHeight: "10%", overflowY: "auto" }}
            onCancel={() => setView(false)}
            width={650}
          >
            <ViewPrescription
              viewPrescription={viewData}
            />
          </Modal>
        </Col>
      </Row>
      <HistoricalMedication />
      {prescriptionDrawer && <PrescriptionPad
        visible={prescriptionDrawer}
        rxData={prescriptionData}
        officeData={officeSetting}
        ptData={patientBasicInfo}
        onClose={onClose}
      />}
    </div>
  );
};

export default AllMedications;
